import { Divider, Flex, Group, SegmentedControl, Select } from '@byecode/ui'
import { WechatProfitSharingRelationTypeEnum, WechatProfitSharingTypeEnum } from '@lighthouse/core'
import {
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WeChatAddProfitSharingReceiverActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { WechatPaymentAccountSelect } from '@/components/WechatPaymentAccountSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import * as CM from '../../styles'
import {
    PROFIT_SHARING_PLATFORM_OPTIONS,
    PROFIT_SHARING_RELATION_TYPE_OPTIONS,
    PROFIT_SHARING_TYPE_OPTIONS
} from '../WeChatProfitSharingActionConfigure/constants'

interface WeChatAddProfitSharingReceiverActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WeChatAddProfitSharingReceiverActionConfig
    onChange: (value: WeChatAddProfitSharingReceiverActionConfig) => void
}

export const WeChatAddProfitSharingReceiverActionConfigure: React.FC<WeChatAddProfitSharingReceiverActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const profitSharingType = value.type
    const profitSharingRelationType = value.relationType

    const singleRecordNodes = useMemo(() => {
        return allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType))
    }, [allParentNodes])

    const textUserOption = useGetUserRecordOptions()

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleRecordNodes, workflowList]
    )

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    return (
        <CM.Container>
            {header}
            <WechatPaymentAccountSelect
                value={value?.paymentAccountId}
                onChange={paymentAccountId => onChange({ ...value, paymentAccountId })}
                requireEnabled
            />
            <Divider />
            <Group label="分账接收方">
                <CM.FieldList>
                    <CM.FieldItem>
                        <Flex gap={16}>
                            <CM.FieldTitle required>分账接收方类型</CM.FieldTitle>
                        </Flex>
                        <Select
                            style={{ width: '100%' }}
                            options={PROFIT_SHARING_TYPE_OPTIONS}
                            value={value.type}
                            onChange={type => onChange({ ...value, type: type as WeChatAddProfitSharingReceiverActionConfig['type'] })}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="profitSharingAccount"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>分账接收方账号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        {profitSharingType === WechatProfitSharingTypeEnum.PERSONAL_OPENID && (
                            <SegmentedControl
                                fullWidth
                                data={PROFIT_SHARING_PLATFORM_OPTIONS}
                                value={value.platform}
                                onChange={platform =>
                                    onChange({ ...value, platform: platform as WeChatAddProfitSharingReceiverActionConfig['platform'] })
                                }
                            />
                        )}
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.account}
                            onBlur={account => onChange({ ...value, account })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <Flex gap={16}>
                            <PopoverQuestion
                                type="profitSharingReceiverName"
                                renderTarget={(node, ref) => (
                                    <Flex gap={16} ref={ref} alignItems="center">
                                        <CM.FieldTitle required={profitSharingType === WechatProfitSharingTypeEnum.MERCHANT_ID}>
                                            分账接收方全称
                                        </CM.FieldTitle>
                                        {node}
                                    </Flex>
                                )}
                            />
                        </Flex>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.name}
                            onBlur={name => onChange({ ...value, name })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <Flex gap={16}>
                            <PopoverQuestion
                                type="profitSharingReceiverRelationType"
                                renderTarget={(node, ref) => (
                                    <Flex gap={16} ref={ref} alignItems="center">
                                        <CM.FieldTitle required>与分账方的关系类型</CM.FieldTitle>
                                        {node}
                                    </Flex>
                                )}
                            />
                        </Flex>
                        <Select
                            style={{ width: '100%' }}
                            options={PROFIT_SHARING_RELATION_TYPE_OPTIONS}
                            value={value.relationType}
                            onChange={relationType =>
                                onChange({
                                    ...value,
                                    relationType: relationType as WeChatAddProfitSharingReceiverActionConfig['relationType']
                                })
                            }
                        />
                    </CM.FieldItem>
                    {profitSharingRelationType === WechatProfitSharingRelationTypeEnum.CUSTOM && (
                        <CM.FieldItem>
                            <Flex gap={16}>
                                <PopoverQuestion
                                    type="profitSharingReceiverCustomRelation"
                                    renderTarget={(node, ref) => (
                                        <Flex gap={16} ref={ref} alignItems="center">
                                            <CM.FieldTitle required>自定义的分账关系</CM.FieldTitle>
                                            {node}
                                        </Flex>
                                    )}
                                />
                            </Flex>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                value={value.customRelation}
                                onBlur={customRelation => onChange({ ...value, customRelation })}
                                autoHeight
                                userOption={textUserOption}
                                upstreamOptions={textUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    )}
                </CM.FieldList>
            </Group>
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_ADD_RECEIVER} />
        </CM.Container>
    )
}
